import { useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Boxes, ChevronRight, Plus, Rotate3d, Trash2, TriangleAlert, Upload } from 'lucide-react'
import { useRole } from '@/app/role'
import { useCurrentSite } from '@/app/sites'
import { PageHeader } from '@/app/shell/PageHeader'
import {
  useCreateAsset,
  useDeleteAsset,
  useSiteAssets,
  useUploadSchematic,
} from '@/lib/api/twin'
import { Button } from '@/ui/button'
import { Card, KLabel } from '@/ui/card'
import { RiskChip } from '@/ui/chip'
import { PackBadge } from '@/ui/pack-badge'
import { EmptyState, LoadingBlock } from '@/ui/states'
import { lcMeta, TYPE_OPTIONS, TypeIcon, typeLabel } from './meta'

export function AssetsPage() {
  const { role } = useRole()
  const { site, siteId } = useCurrentSite()
  const id = siteId ?? ''
  const nav = useNavigate()
  const assets = useSiteAssets(id)
  const create = useCreateAsset(id)
  const del = useDeleteAsset(id)
  const upload = useUploadSchematic(id)
  const canEdit = role === 'assessor' || role === 'owner'

  const fileRef = useRef<HTMLInputElement>(null)
  const [adding, setAdding] = useState(false)
  const [name, setName] = useState('')
  const [type, setType] = useState(TYPE_OPTIONS[0].id)
  const [capacity, setCapacity] = useState('')
  const [location, setLocation] = useState('')
  const [err, setErr] = useState<string>()

  const rows = [...(assets.data ?? [])].sort((a, b) => a.asset_id.localeCompare(b.asset_id))
  const count = (s: string) => rows.filter((r) => r.lifecycle_state === s).length

  const add = () => {
    if (!name.trim()) return
    create.mutate(
      { name: name.trim(), asset_type: type, capacity: capacity || undefined, location: location || undefined },
      { onSuccess: () => { setName(''); setCapacity(''); setLocation(''); setAdding(false) } },
    )
  }
  const remove = (assetId: string) => {
    setErr(undefined)
    del.mutate(assetId, {
      onError: (e: any) =>
        setErr(
          e?.response?.status === 409
            ? 'That asset is referenced by assessment responses — decommission it from its detail page instead.'
            : 'Could not delete the asset.',
        ),
    })
  }

  return (
    <>
      <PageHeader
        title="Assets"
        subtitle={
          <>
            {site && <PackBadge pack={site.pack} />}
            <span>{rows.length} assets</span>
            {count('impaired') > 0 && (
              <>
                <span className="text-faint">·</span>
                <span className="text-flare">{count('impaired')} impaired</span>
              </>
            )}
          </>
        }
        actions={
          <>
            <Button variant="secondary" onClick={() => nav(`/site/${id}/twin`)}>
              <Rotate3d /> 3D twin
            </Button>
            {canEdit && (
              <>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*,application/pdf"
                  hidden
                  onChange={(e) => {
                    const f = e.target.files?.[0]
                    if (f) upload.mutate(f)
                    e.target.value = ''
                  }}
                />
                <Button variant="secondary" disabled={upload.isPending} onClick={() => fileRef.current?.click()}>
                  <Upload /> {upload.isPending ? 'Uploading…' : 'Upload schematic'}
                </Button>
                <Button onClick={() => setAdding((v) => !v)}>
                  <Plus /> Add asset
                </Button>
              </>
            )}
          </>
        }
      />

      {adding && canEdit && (
        <Card className="mb-4">
          <KLabel className="mb-3">Register an asset</KLabel>
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[200px] flex-1">
              <label className="mb-1 block text-[12px] font-medium">Name</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && add()}
                placeholder="e.g. Rack C"
                className="h-9 w-full rounded-md border border-input bg-surface-2 px-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-ring"
              />
            </div>
            <div>
              <label className="mb-1 block text-[12px] font-medium">Type</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="h-9 rounded-md border border-input bg-surface-2 px-2 text-sm outline-none focus:border-primary"
              >
                {TYPE_OPTIONS.map((t) => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-[12px] font-medium">Capacity</label>
              <input
                value={capacity}
                onChange={(e) => setCapacity(e.target.value)}
                placeholder="3.2 MWh"
                className="h-9 w-28 rounded-md border border-input bg-surface-2 px-3 text-sm outline-none focus:border-primary"
              />
            </div>
            <div>
              <label className="mb-1 block text-[12px] font-medium">Location</label>
              <input
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Hall 1"
                className="h-9 w-32 rounded-md border border-input bg-surface-2 px-3 text-sm outline-none focus:border-primary"
              />
            </div>
            <Button onClick={add} disabled={!name.trim() || create.isPending}>
              <Plus /> {create.isPending ? 'Adding…' : 'Add'}
            </Button>
          </div>
          <p className="mt-2 text-[11.5px] text-faint">
            A registered asset appears in Operations (live telemetry attaches) and can be placed on the 3D twin.
          </p>
        </Card>
      )}

      {err && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-ignition/40 bg-ignition/[0.08] px-3 py-2.5 text-[13px]">
          <TriangleAlert className="mt-0.5 size-4 shrink-0 text-ignition" />
          <span>{err}</span>
        </div>
      )}

      {assets.isLoading ? (
        <Card><LoadingBlock lines={5} /></Card>
      ) : rows.length === 0 ? (
        <EmptyState kind="no-data" title="No assets registered">
          Register the site’s assets to build its 3D twin and attach live telemetry.
        </EmptyState>
      ) : (
        <Card className="overflow-hidden p-0">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-faint">
                <th className="px-4 py-3 font-semibold">Asset</th>
                <th className="px-4 py-3 font-semibold">Type</th>
                <th className="px-4 py-3 font-semibold">Location</th>
                <th className="px-4 py-3 font-semibold">Lifecycle</th>
                <th className="px-4 py-3 font-semibold">Capacity</th>
                <th className="px-4 py-3 font-semibold">SCADA</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => {
                const lc = lcMeta(a.lifecycle_state)
                const Icon = TypeIcon(a.asset_type)
                const live = a.lifecycle_state === 'operational' || a.lifecycle_state === 'impaired'
                const busy = del.isPending && del.variables === a.asset_id
                return (
                  <tr
                    key={a.asset_id}
                    onClick={() => nav(`/site/${id}/assets/${a.asset_id}`)}
                    className="cursor-pointer border-t hover:bg-secondary/50"
                  >
                    <td className="px-4 py-2.5">
                      <span className="font-mono text-[10.5px] text-faint">{a.asset_id}</span>
                      <span className="ml-1.5 font-medium">{a.name}</span>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                        <Icon className="size-3.5" /> {typeLabel(a.asset_type)}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground">{a.location ?? '—'}</td>
                    <td className="px-4 py-2.5"><RiskChip label={lc.label} tone={lc.tone} /></td>
                    <td className="px-4 py-2.5 tabular-nums">{a.capacity ?? '—'}</td>
                    <td className="px-4 py-2.5">
                      {live ? (
                        <span className="inline-flex items-center gap-1.5 text-[11.5px] text-clear">
                          <span className="size-1.5 animate-pulse rounded-full bg-clear" /> live
                        </span>
                      ) : (
                        <span className="text-faint">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        {canEdit && (
                          <Button size="sm" variant="ghost" disabled={busy} onClick={() => remove(a.asset_id)}>
                            <Trash2 />
                          </Button>
                        )}
                        <ChevronRight className="size-4 text-faint" />
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </Card>
      )}

      <p className="mt-4 flex max-w-[80ch] items-start gap-2 text-[12px] text-faint">
        <Boxes className="mt-0.5 size-3.5 shrink-0" />
        <span>
          The register is the source of truth for the <Link to={`/site/${id}/twin`} className="font-medium text-muted-foreground underline-offset-2 hover:underline">3D twin</Link>.
          Assets are never hard-deleted while responses reference them — they are decommissioned.
        </span>
      </p>
    </>
  )
}
